import { MIN_GRID_GAP } from '~/features/grid/constants';
import { resizeCropEdge } from '~/features/grid/cropEdge';
import { resizeBBoxByDelta } from '~/features/pin/resizeBBox';
import { useShortcut } from '~/features/shortcuts/useShortcut';
import { clamp } from '~/lib/utils';
import { useStore } from '~/store';

export function dispatchNudge(axis: 'x' | 'y', delta: number) {
  const s = useStore.getState();
  const sel = s.editor.selectedEdge;
  if (!sel) return;
  const dims = s.source.dimensions;
  const crop = s.grid.cropRect;
  const cols = s.grid.colDividers ?? (crop ? [crop.x, crop.x + crop.w] : []);
  const rows = s.grid.rowDividers ?? (crop ? [crop.y, crop.y + crop.h] : []);

  if (sel.kind === 'cropEdge' && crop && dims) {
    const sideAxis: 'x' | 'y' = sel.side === 'left' || sel.side === 'right' ? 'x' : 'y';
    if (axis !== sideAxis) return;
    let currentEdge = 0;
    if (sel.side === 'left') currentEdge = crop.x;
    else if (sel.side === 'right') currentEdge = crop.x + crop.w;
    else if (sel.side === 'top') currentEdge = crop.y;
    else currentEdge = crop.y + crop.h;
    s.setCropRect(resizeCropEdge(crop, dims, sel.side, currentEdge + delta));
    return;
  }

  if (
    (sel.kind === 'pinBlob' || sel.kind === 'pinPivot' || sel.kind === 'pinEdge') &&
    s.pin.framingMode === 'pin'
  ) {
    const blob = s.pin.blobs?.[sel.index];
    if (!blob) return;
    const override = s.pin.overrides[sel.index] ?? {};
    const d = axis === 'x' ? { x: delta, y: 0 } : { x: 0, y: delta };
    if (sel.kind === 'pinBlob') {
      const bbox = override.bbox ?? blob.bbox;
      const pivot = override.pivot ?? blob.centroid;
      s.setBlobOverride(sel.index, {
        bbox: { x: bbox.x + d.x, y: bbox.y + d.y, w: bbox.w, h: bbox.h },
        pivot: { x: pivot.x + d.x, y: pivot.y + d.y },
      });
      return;
    }
    const pivot = override.pivot ?? blob.centroid;
    if (sel.kind === 'pinPivot') {
      s.setBlobOverride(sel.index, {
        pivot: { x: pivot.x + d.x, y: pivot.y + d.y },
      });
      return;
    }
    const bbox = override.bbox ?? blob.bbox;
    if ((sel.side === 'left' || sel.side === 'right') && axis !== 'x') return;
    if ((sel.side === 'top' || sel.side === 'bottom') && axis !== 'y') return;
    s.setBlobOverride(sel.index, {
      bbox: resizeBBoxByDelta(bbox, sel.side, delta),
    });
    return;
  }

  if (sel.kind === 'colDivider' && cols.length >= 2 && axis === 'x') {
    const i = sel.index;
    if (i <= 0 || i >= cols.length - 1) return;
    const prev = cols[i - 1];
    const current = cols[i];
    const nextDivider = cols[i + 1];
    if (prev === undefined || current === undefined || nextDivider === undefined) return;
    const next = [...cols];
    next[i] = clamp(prev + MIN_GRID_GAP, nextDivider - MIN_GRID_GAP, current + delta);
    s.setColDividers(next);
    return;
  }

  if (sel.kind === 'rowDivider' && rows.length >= 2 && axis === 'y') {
    const i = sel.index;
    if (i <= 0 || i >= rows.length - 1) return;
    const prev = rows[i - 1];
    const current = rows[i];
    const nextDivider = rows[i + 1];
    if (prev === undefined || current === undefined || nextDivider === undefined) return;
    const next = [...rows];
    next[i] = clamp(prev + MIN_GRID_GAP, nextDivider - MIN_GRID_GAP, current + delta);
    s.setRowDividers(next);
    return;
  }
}

export function useNudge() {
  useShortcut('nudgeUp', () => dispatchNudge('y', -1));
  useShortcut('nudgeDown', () => dispatchNudge('y', +1));
  useShortcut('nudgeLeft', () => dispatchNudge('x', -1));
  useShortcut('nudgeRight', () => dispatchNudge('x', +1));
  useShortcut('nudgeUpBig', () => dispatchNudge('y', -10));
  useShortcut('nudgeDownBig', () => dispatchNudge('y', +10));
  useShortcut('nudgeLeftBig', () => dispatchNudge('x', -10));
  useShortcut('nudgeRightBig', () => dispatchNudge('x', +10));
}
